import React, { useCallback, useEffect, useRef } from 'react';

import api from '../../services/api';
import { getProductSku, resolveShippingSnapshot } from '../../services/productSelection';
import { useAppDispatch, useAppSelector } from '../../store/hooks';
import { syncCartMetadata } from '../../store/slices/cartSlice';
import { IProduct } from '../../store/slices/productSlice';
import { CartItem } from '../../types/cart';

const CART_SYNC_INTERVAL_MS = 5 * 60 * 1000;

const getCartProductIds = (items: CartItem[]) =>
  Array.from(
    new Set(
      items
        .map((item) => String(item.productId || '').trim())
        .filter((productId) => productId && productId !== 'undefined' && productId !== 'null'),
    ),
  );

const CartDataSync: React.FC = () => {
  const dispatch = useAppDispatch();
  const cartItems = useAppSelector((state) => state.cart.items);
  const isCartHydrated = useAppSelector((state) => state.cart.isHydrated);
  const inFlightRef = useRef(false);

  const syncCartItems = useCallback(async () => {
    const productIds = getCartProductIds(cartItems);
    if (!productIds.length || inFlightRef.current) {
      return;
    }

    inFlightRef.current = true;

    try {
      const responses = await Promise.allSettled(
        productIds.map(async (productId) => {
          const { data } = await api.get(`/api/products/${productId}`);
          return [productId, data as IProduct] as const;
        }),
      );

      const productsById = new Map<string, IProduct>();
      responses.forEach((result) => {
        if (result.status === 'fulfilled') {
          const [productId, product] = result.value;
          productsById.set(productId, product);
        }
      });

      const updates: Array<{
        key: string;
        sku?: string;
        itemSnapshot?: CartItem['itemSnapshot'];
      }> = [];

      for (const item of cartItems) {
        const product = productsById.get(String(item.productId || '').trim());
        if (!product) {
          continue;
        }

        const nextSnapshot = resolveShippingSnapshot(product, {
          variantName: item.options?.variant,
          size: item.options?.size,
        });

        const snapshotChanged =
          nextSnapshot.weight_grams !== (item.itemSnapshot?.weight_grams ?? null) ||
          nextSnapshot.package_length_cm !== (item.itemSnapshot?.package_length_cm ?? null) ||
          nextSnapshot.package_width_cm !== (item.itemSnapshot?.package_width_cm ?? null) ||
          nextSnapshot.package_height_cm !== (item.itemSnapshot?.package_height_cm ?? null);

        const nextSku = getProductSku(product, {
          variantName: item.options?.variant,
          size: item.options?.size,
        });
        const skuChanged = nextSku !== item.sku;

        if (!snapshotChanged && !skuChanged) {
          continue;
        }

        updates.push({
          key: item.key,
          sku: nextSku,
          itemSnapshot: nextSnapshot,
        });
      }

      if (updates.length > 0) {
        dispatch(syncCartMetadata(updates));
      }
    } catch {
      // Silent fallback: checkout/cart will still show explicit validation hints.
    } finally {
      inFlightRef.current = false;
    }
  }, [cartItems, dispatch]);

  useEffect(() => {
    if (!isCartHydrated || !cartItems.length) {
      return;
    }

    syncCartItems();
  }, [cartItems.length, isCartHydrated, syncCartItems]);

  useEffect(() => {
    if (!isCartHydrated || !cartItems.length) {
      return;
    }

    const intervalId = window.setInterval(() => {
      syncCartItems();
    }, CART_SYNC_INTERVAL_MS);

    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible') {
        syncCartItems();
      }
    };

    const handleWindowFocus = () => {
      syncCartItems();
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    window.addEventListener('focus', handleWindowFocus);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', handleVisibilityChange);
      window.removeEventListener('focus', handleWindowFocus);
    };
  }, [cartItems.length, isCartHydrated, syncCartItems]);

  return null;
};

export default CartDataSync;
